import { FC, useEffect, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import {
  Box, Grid, Table, TableBody, TableHead, TableRow, TableCell, tableCellClasses, Chip, Button, CircularProgress,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchUnits } from "../../store/unitsStore/unitsThunk";
import { useInfiniteReport } from "../../hooks/useInfiniteReport";
import ReusableReportHeader from "../Reports/ReusableReportHeader";
import LocationSelect from "./pickers/LocationSelect";
import { FilterSelect } from "./InvFilters";
import Filters from "../Filters/Filters";

// Requisition report — same pattern as the Stock Transfer Report (reused
// ReusableReportHeader + styled printable table). Rows are EXPANDED to line-item
// level: each requisition's header spans its item rows (rowSpan) and every SKU line
// shows requested / approved / issued qty + unit — the full requisition document.
// Data is server-paginated (50/page) via infinite scroll; all filters are
// server-side; export pages through the full set.

const STATUS_COLOR: Record<string, string> = {
  draft: "#b08900", submitted: "#488dff", approved: "#49a563",
  partially_fulfilled: "#0d9488", fulfilled: "#2e7d32", rejected: "#c33b3b",
};
const REQ_STATUSES = ["draft", "submitted", "approved", "partially_fulfilled", "fulfilled", "rejected"];
const statusLabel = (s: string) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#eaecf0", color: "#000", border: "0.75px solid #d9d4d4",
    padding: 5, fontSize: "9pt", paddingLeft: "12px", fontWeight: 600, whiteSpace: "nowrap",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "9pt", fontWeight: "normal", color: "#292d35",
    padding: 5, border: "0.75px solid #d9d4d4", paddingLeft: "12px", verticalAlign: "top",
  },
}));

const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString("en-GB") : "-");
const num = (n: any) => Number(n ?? 0);
const esc = (s: any) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));

// Header (requisition-level, spanned) + item-level columns.
const HEADER_COLS = ["Date", "Req #", "From (Requester)", "To (Fulfiller)", "Status", "Created By", "Requested By", "Approved By" /*, "Notes" */];
const ITEM_COLS = ["Item" /* item code shown under the name */, "Requested", "Approved", "Issued", "Unit"];
const ALL_COLS = [...HEADER_COLS, ...ITEM_COLS];

const RequisitionReport: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const unitsById = useAppSelector((state) => state.units.byId);
  const unitsList = useAppSelector((state) => state.units.units);

  // Item `unit` is stored as a MeasuringUnit id — resolve to a readable code/name.
  const unitText = (u?: string) => (u ? unitsById?.[u]?.code || unitsById?.[u]?.name || u : "-");

  const [fromLocationId, setFromLocationId] = useState("");
  const [toLocationId, setToLocationId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  // Bumped on reset to remount the shared date-range dropdown back to "Select Date".
  const [filtersKey, setFiltersKey] = useState(0);

  useEffect(() => {
    if (!unitsList || unitsList.length === 0) dispatch(fetchUnits());
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Server-side, scroll-paginated data (50 records/page).
  const { rows, total, loading, loadingMore, hasMore, loadMore, collectAll } = useInfiniteReport(
    "/api/v1/erp/requisitions",
    { fromLocationId, toLocationId, status: statusFilter, startDate, endDate }
  );

  // Shared Filters dropdown emits "start=YYYY-MM-DD&end=YYYY-MM-DD" (empty for "Select Date").
  const handleDateFilter = (qs: string) => {
    const params = new URLSearchParams(qs);
    unstable_batchedUpdates(() => {
      setStartDate(params.get("start") || "");
      setEndDate(params.get("end") || "");
    });
  };

  const resetFilters = () => {
    setFromLocationId(""); setToLocationId(""); setStatusFilter(""); setStartDate(""); setEndDate("");
    setFiltersKey((k) => k + 1);
  };

  const onScroll = (e: any) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 300 && hasMore && !loadingMore && !loading) loadMore();
  };

  const rangeText = startDate || endDate ? `${startDate ? fmtDate(startDate) : "…"} – ${endDate ? fmtDate(endDate) : "…"}` : "All dates";

  // -------- export (pages through the FULL filtered set, not just what's loaded) --------
  const flatRows = (data: any[]): string[][] =>
    data.flatMap((r: any) => {
      const items = (r.items && r.items.length) ? r.items : [null];
      return items.map((it: any): string[] => [
        fmtDate(r.createdAt), r.reqNumber || "", r.fromLocationId?.name || "", r.toLocationId?.name || "",
        statusLabel(r.status || ""), r.createdBy?.name || "", r.requestedBy?.name || "", r.approvedBy?.name || "", /* r.notes || "", */
        it?.skuId ? `${it.skuId.name || ""}${it.skuId.itemCode ? ` (${it.skuId.itemCode})` : ""}` : "",
        it ? String(num(it.qtyRequested)) : "", it ? String(num(it.qtyApproved)) : "",
        it ? String(num(it.qtyIssued)) : "", it ? unitText(it.unit) : "",
      ]);
    });

  const buildTableHtml = (data: any[]) => `
    <table border="1" cellspacing="0" cellpadding="5" style="border-collapse:collapse;font-family:Arial;font-size:11px">
      <thead><tr style="background:#eaecf0">${ALL_COLS.map((c) => `<th align="left">${esc(c)}</th>`).join("")}</tr></thead>
      <tbody>${flatRows(data).map((r) => `<tr>${r.map((v) => `<td>${esc(v)}</td>`).join("")}</tr>`).join("")}</tbody>
    </table>`;

  const handleDownloadExcel = async () => {
    const all = await collectAll();
    const html = `<html><head><meta charset="utf-8" /></head><body>${buildTableHtml(all)}</body></html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Requisition_Report_${new Date().toISOString().split("T")[0]}.xls`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const handleDownloadPDF = async () => {
    const all = await collectAll();
    const lines = all.reduce((s: number, r: any) => s + Math.max((r.items || []).length, 1), 0);
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Requisition Report</title>
      <style>body{font-family:Arial;padding:16px} h3{margin:0 0 4px} .sub{color:#666;font-size:12px;margin-bottom:12px}
      table{border-collapse:collapse;width:100%;font-size:11px} th,td{border:0.75px solid #d9d4d4;padding:5px;text-align:left}
      thead tr{background:#eaecf0}</style></head>
      <body><h3>Requisition Report</h3><div class="sub">${esc(rangeText)} · ${all.length} requisitions · ${lines} lines</div>
      ${buildTableHtml(all)}</body></html>`;
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:absolute;width:0;height:0;border:0;left:-9999px;";
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow?.document;
    if (!doc) return;
    doc.open(); doc.write(html); doc.close();
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow?.focus(); iframe.contentWindow?.print();
        setTimeout(() => document.body.removeChild(iframe), 500);
      }, 300);
    };
  };

  // Dashboard report-card's download button opens this page with ?download=excel|pdf —
  // trigger that export once and clean the URL.
  useEffect(() => {
    const dl = new URLSearchParams(window.location.search).get("download");
    if (!dl) return;
    window.history.replaceState({}, "", window.location.pathname);
    const t = setTimeout(() => { dl === "pdf" ? handleDownloadPDF() : handleDownloadExcel(); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ width: "100%", pr: 1 }}>
      <Box sx={{ width: "100%", overflow: "hidden", mb: 2 }} className={"c-card2"}>
        <ReusableReportHeader
          reportType="requisition"
          title="Requisition Report"
          subtitle={`${rangeText} · ${total} requisitions`}
          recordCount={total}
          isLoading={loading}
          backTo="/inventory/requisitions"
          showPrint={false}
          showShare={false}
          onDownloadPDF={handleDownloadPDF}
          onDownloadExcel={handleDownloadExcel}
        />

        {/* Filters strip */}
        <Grid container spacing={1.5} alignItems="center" p={2} style={{ borderBottom: ".5px solid #9da7b7" }}>
          <Grid item>
            <LocationSelect borderless width={180} value={fromLocationId} onChange={(v: string) => setFromLocationId(v)} label="From (requester)" activeOnly={false} />
          </Grid>
          <Grid item>
            <LocationSelect borderless width={180} value={toLocationId} onChange={(v: string) => setToLocationId(v)} label="To (fulfiller)" activeOnly={false} />
          </Grid>
          <Grid item>
            <FilterSelect
              value={statusFilter}
              onChange={(v: string) => setStatusFilter(v)}
              placeholder="Status"
              width={170}
              options={REQ_STATUSES.map((s) => ({ label: statusLabel(s), value: s }))}
            />
          </Grid>
          <Grid item>
            <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
              <span style={{ position: "absolute", top: -8, left: 20, padding: "0 5px", background: "#fff", fontSize: 12, color: "#6b7280", lineHeight: 1, zIndex: 1, pointerEvents: "none" }}>Date</span>
              <Filters key={filtersKey} type="inventory-requisition" dateOnly hidePartySearch hideFilter hideReset filterHandler={handleDateFilter} />
            </div>
          </Grid>
          <Grid item>
            <Button size="small" color="inherit" startIcon={<RestartAltIcon />} onClick={resetFilters} sx={{ color: "#7a8290" }}>
              Reset
            </Button>
          </Grid>
        </Grid>

        {/* Report table — one requisition header spans its line items; scroll loads more */}
        <div onScroll={onScroll} style={{ maxHeight: "62vh", overflow: "auto" }}>
          <Table stickyHeader sx={{ minWidth: 1400 }} size="small" aria-label="requisition report">
            <TableHead>
              <TableRow>
                {ALL_COLS.map((c) => <StyledTableCell key={c}>{c}</StyledTableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length > 0 ? rows.map((r: any) => {
                const items = (r.items && r.items.length) ? r.items : [null];
                const span = items.length;
                return items.map((it: any, idx: number) => (
                  <TableRow key={`${r._id}-${idx}`}>
                    {idx === 0 && (
                      <>
                        <StyledTableCell rowSpan={span}>{fmtDate(r.createdAt)}</StyledTableCell>
                        <StyledTableCell rowSpan={span}>
                          <span style={{ color: "#488dff", cursor: "pointer" }} onClick={() => navigate(`/inventory/requisitions/${r._id}`)}>
                            {r.reqNumber}
                          </span>
                        </StyledTableCell>
                        <StyledTableCell rowSpan={span}>{r.fromLocationId?.name || "-"}</StyledTableCell>
                        <StyledTableCell rowSpan={span}>{r.toLocationId?.name || "-"}</StyledTableCell>
                        <StyledTableCell rowSpan={span}>
                          <Chip size="small" label={statusLabel(r.status || "")} sx={{ color: STATUS_COLOR[r.status] || "#555" }} />
                        </StyledTableCell>
                        <StyledTableCell rowSpan={span}>{r.createdBy?.name || "-"}</StyledTableCell>
                        <StyledTableCell rowSpan={span}>{r.requestedBy?.name || "-"}</StyledTableCell>
                        <StyledTableCell rowSpan={span}>{r.approvedBy?.name || "-"}</StyledTableCell>
                        {/* <StyledTableCell rowSpan={span} style={{ maxWidth: 220, whiteSpace: "pre-wrap" }}>{r.notes || "-"}</StyledTableCell> */}
                      </>
                    )}
                    <StyledTableCell>
                      {it?.skuId?.name || "-"}
                      {it?.skuId?.itemCode ? (
                        <div style={{ color: "#9da7b7", fontSize: "8pt", marginTop: 2 }}>{it.skuId.itemCode}</div>
                      ) : null}
                    </StyledTableCell>
                    <StyledTableCell>{it ? num(it.qtyRequested) : "-"}</StyledTableCell>
                    <StyledTableCell>{it ? num(it.qtyApproved) : "-"}</StyledTableCell>
                    <StyledTableCell>{it ? num(it.qtyIssued) : "-"}</StyledTableCell>
                    <StyledTableCell>{it ? unitText(it.unit) : "-"}</StyledTableCell>
                  </TableRow>
                ));
              }) : (
                <TableRow>
                  <StyledTableCell colSpan={ALL_COLS.length} style={{ textAlign: "center", color: "#9da7b7", padding: 24 }}>
                    {loading ? "Loading…" : "No requisitions match the selected filters."}
                  </StyledTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {loadingMore ? (
            <div style={{ textAlign: "center", padding: 12 }}><CircularProgress size={20} /></div>
          ) : (!hasMore && rows.length > 0) ? (
            <div style={{ textAlign: "center", padding: 10, color: "#c7ccd4", fontSize: 12 }}>All {total} requisitions loaded</div>
          ) : null}
        </div>
      </Box>
    </Box>
  );
};

export default RequisitionReport;
